import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardBody,
  Button,
  Spinner,
  Alert,
  Progress
} from 'reactstrap';

const Menores = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Iniciando conexión...');

  // Simulador de progreso de carga
  useEffect(() => {
    if (!loading) return;

    const progressSteps = [
      { progress: 20, text: 'Conectando con Looker Studio...', delay: 500 },
      { progress: 45, text: 'Autenticando usuario...', delay: 800 },
      { progress: 70, text: 'Cargando datos del dashboard...', delay: 1200 },
      { progress: 90, text: 'Finalizando carga...', delay: 1500 }
    ];

    progressSteps.forEach(({ progress, text, delay }) => {
      setTimeout(() => {
        if (loading) {
          setLoadingProgress(progress);
          setLoadingText(text);
        }
      }, delay);
    });

    // Timeout de seguridad para ocultar el loading después de 5 segundos
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [loading]);

  const handleIframeLoad = () => {
    setTimeout(() => {
      setLoading(false);
      setLoadingProgress(100);
    }, 800); // Pequeño delay para suavizar la transición
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
  };

  const getIframeUrl = () => {
    const baseUrl = "https://datastudio.google.com/embed/reporting/5a0bd211-19e3-4aee-a18e-9ee3cb2a1d7a/page/p_m91h1udgtd";
                     
    if (user && user.email) {
      const params = new URLSearchParams({
        authuser: user.email,
        usp: 'sharing',
      });
      return `${baseUrl}?${params.toString()}`;
    }
    return baseUrl;
  };

  const openInNewTab = () => {
    window.open(getIframeUrl(), '_blank');
  };

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Alertas de error */}
      {error && (
        <Alert color="danger" className="m-3" style={{ borderRadius: '12px' }}>
          <h6 className="alert-heading fw-bold">⚠️ Error de carga</h6>
          No se pudo cargar el dashboard. Por favor, verifica tu conexión a internet e intenta nuevamente.
          <hr />
          <Button color="danger" size="sm" onClick={() => window.location.reload()} className="me-2">
            🔄 Reintentar
          </Button>
        </Alert>
      )}

      {/* Dashboard Container */}
      <div style={{ 
        position: 'relative',
        width: '100%',
        height: '100vh',
        minHeight: '600px',
        overflow: 'auto',
        padding: '20px',
        boxSizing: 'border-box'
      }}>

        {/* Iframe del dashboard con margen */}
        <div style={{
          width: '100%',
          height: 'calc(100vh - 40px)',
          minHeight: '560px',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: '1px solid rgba(0,0,0,0.1)',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.5s ease-in-out'
        }}>
          <iframe
            id="menores-iframe"
            src={getIframeUrl()}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '16px'
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            title="Dashboard Menores - Looker Studio"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
          />
        </div>
        
        {/* Splash Screen Mejorado */}
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#0f4f47',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            color: 'white'
          }}>
            {/* Logo/Icono animado */}
            <div style={{
              marginBottom: '2rem',
              animation: 'pulse 2s infinite'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📊
              </div>
            </div>

            {/* Spinner personalizado */}
            <div style={{
              position: 'relative',
              marginBottom: '2rem'
            }}>
              <Spinner 
                color="light" 
                style={{ 
                  width: '3rem', 
                  height: '3rem',
                  borderWidth: '3px'
                }} 
              />
            </div>

            {/* Título principal */}
            <h2 style={{
              fontWeight: 'bold',
              marginBottom: '1rem',
              textAlign: 'center',
              fontSize: '1.8rem',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              Dashboard Menores
            </h2>

            {/* Texto de estado dinámico */}
            <p style={{
              fontSize: '1.1rem',
              marginBottom: '2rem',
              textAlign: 'center',
              opacity: 0.9,
              maxWidth: '350px',
              lineHeight: '1.5'
            }}>
              {loadingText}
            </p>

            {/* Barra de progreso */}
            <div style={{
              width: '100%',
              maxWidth: '320px',
              marginBottom: '2rem'
            }}>
              <Progress 
                value={loadingProgress} 
                style={{
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }}
                barStyle={{
                  backgroundColor: '#a7d0c6',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease'
                }}
              />
              <div style={{
                textAlign: 'center',
                marginTop: '0.5rem',
                fontSize: '0.9rem',
                opacity: 0.8
              }}>
                {loadingProgress}% completado
              </div>
            </div>

            {/* Información adicional */}
            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              opacity: 0.7,
              maxWidth: '300px'
            }}>
              Desarrollado por Nuvia Consulting
            </div>

          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </Container>
  );
};

export default Menores;